<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="6" lg="5" xl="4" class="px-4 px-sm-6">
        <v-card class="otp-card pa-4 pa-sm-6 text-center rounded-lg elevation-10">
          <h2 class="mb-2 font-weight-bold text-h5">Enter OTP Code</h2>
          <div v-if="maskedEmail" class="mb-4 text-body-2 grey--text text--darken-1">
            Code sent to <span class="teal--text text--darken-2 font-weight-medium">{{ maskedEmail }}</span>
          </div>

          <div class="otp-container d-flex justify-center mb-4">
            <input
              v-for="(digit, index) in otp"
              :key="index"
              ref="otpInputs"
              v-model="otp[index]"
              @input="onInput(index)"
              @paste="onPaste($event)"
              @keydown="onKeyDown(index, $event)"
              :disabled="isSuspended"
              maxlength="1"
              type="text"
              pattern="\d*"
              inputmode="numeric"
              class="otp-box"
              :aria-label="`Digit ${index + 1}`"
            />
          </div>

          <div class="mb-4 text-caption">
            <div>Didn't receive the code?</div>
            <div v-if="timer > 0">You can resend in {{ formattedTime }}</div>
            <button
              type="button"
              class="resend-text mt-1"
              @click="resendOtp"
              :disabled="timer > 0 || isSuspended"
            >
              Resend OTP
            </button>
          </div>

          <div class="d-flex justify-center mt-10 btn-gap">
            <v-btn dark color="secondary" @click="goBack" large>Back</v-btn>
            <v-btn
              color="#005f7a"
              class="white--text"
              large
              :disabled="isSuspended || otp.join('').length < 6"
              @click="verifyOtp"
            >
              Verify
            </v-btn>
          </div>

        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { date, showAlert } from '../../lib/helpers';

export default {
  name: "LoginOTP",
  data() {
    return {
      otp: ["", "", "", "", "", ""],
      timer: 0,
      intervalId: null,
      isSuspended: false,
    };
  },
  beforeCreate() {
    if (this.$store.state.authenticated) {
      this.$router.push({ name: 'Dashboard' });
    }
  },
  computed: {
    expiredOtp() {
      const otpObj = this.$store.state.expiredOtp;
      if (!otpObj || !otpObj.expired_time || !otpObj.request_time) return null;
      return otpObj;
    },
    maskedEmail() {
      return this.$store.state.expiredOtp?.email || '';
    },
    formattedTime() {
      const min = String(Math.floor(this.timer / 60)).padStart(2, "0");
      const sec = String(this.timer % 60).padStart(2, "0");
      return `${min}:${sec}`;
    },
  },
  methods: {
    handleEnterKey(event) {
      if (event.key === 'Enter') {
        const isComplete = this.otp.join('').length === 6;
        if (isComplete && !this.isSuspended) {
          this.verifyOtp();
        }
      }
    },
    onKeyDown(index, event) {
      if (event.key === 'Backspace' && !this.otp[index] && index > 0) {
        this.$refs.otpInputs[index - 1]?.focus();
      }
    },
    onInput(index) {
      const value = this.otp[index].replace(/\D/g, "");
      this.$set(this.otp, index, value);
      if (value && index < this.otp.length - 1) {
        this.$refs.otpInputs[index + 1]?.focus();
      }
    },
    onPaste(e) {
      const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (paste.length === 6) {
        for (let i = 0; i < 6; i += 1) {
          this.$set(this.otp, i, paste[i]);
        }
        this.$nextTick(() => {
          this.$refs.otpInputs[5].focus();
        });
      }
      e.preventDefault();
    },
    goBack() {
      this.$router.back();
    },
    async verifyOtp() {
      const otpCode = this.otp.join("");

      if (otpCode.length !== 6) {
        showAlert(this.$store, 'Please enter the complete 6-digit OTP code.', 'mdi-alert', 'red');
        return;
      }

      try {
        const stringOtp = String(otpCode);
        const response = await this.$store.dispatch('validateOtp', { stringOtp });

        if (response && response.code === 200) {
          const userData = response.data;
          
          this.$store.dispatch('set_login', { payload: userData });
          
          localStorage.removeItem('otpExpiredTime');
          this.$store.commit('setExpiredOtp', null);

          const needsForceChange = userData.force_password_change === true || userData.force_last_change_password === 1;
          
          if (!needsForceChange) {
            this.$store.dispatch('getNotif', { date });
          }
          
          this.$router.replace('/');
        }
        if (response?.code === 313){
          this.isSuspended = true;

          showAlert(this.$store, response.data, 'mdi-alert', 'red');

        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("OTP Validation Error:", error);

        if (error.response?.status === 403) {
          this.isSuspended = true;

          showAlert(this.$store, error.response.data.message_en, 'mdi-alert', 'red');

          return;
        }

        let errorMsg = 'Server error during OTP validation.';
        if (error.response?.data?.data) {
          errorMsg = error.response.data.data;
        } else if (error.response?.data?.message_en) {
          errorMsg = error.response.data.message_en;
        }

        showAlert(this.$store, errorMsg, 'mdi-alert', 'red');
      }
    },

    async resendOtp() {
      try {
        const response = await this.$store.dispatch('resendOtp');

        if (response?.code === 200) {
          const requestTime = response?.data?.request_time;
          const expiredTime = response?.data?.expired_time;
          const currentEmail = this.$store.state.expiredOtp?.email || response?.data?.email || '';
          const resendCooldownSeconds = response?.data?.resend_cooldown_seconds || 60;

          this.$store.commit('setExpiredOtp', {
            request_time: requestTime,
            expired_time: expiredTime,
            email: currentEmail,
            resend_cooldown_seconds: resendCooldownSeconds
          });

          this.startResendCooldown(resendCooldownSeconds);

          showAlert(this.$store, 'OTP has been resent successfully.', 'mdi-check-circle', 'green');
        } else if (response?.code === 314 || response?.code === 409) {
          const errorMsg = typeof response?.data === 'string' 
            ? response.data 
            : (response?.message_en || 'Please wait before requesting a new OTP.');
          showAlert(this.$store, errorMsg, 'mdi-alert', 'red');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Resend OTP Error:", error);
        showAlert(this.$store, 'Server error while resending OTP.', 'mdi-alert', 'red');
      }
    },
    startResendCooldown(cooldownSeconds) {
      clearInterval(this.intervalId);
      this.timer = cooldownSeconds;

      if (this.timer <= 0) return;

      this.intervalId = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          clearInterval(this.intervalId);
        }
      }, 1000);
    },
    startTimerFromApi(requestTime, expiredTime) {
      clearInterval(this.intervalId);

      const expired = new Date(`${expiredTime.replace(' ', 'T')}+07:00`).getTime();
      const now = Date.now();

      const resendCooldown = this.expiredOtp?.resend_cooldown_seconds || 60;
      const timeLeft = Math.floor((expired - now) / 1000);
      const safeTime = Math.min(Math.max(timeLeft, 0), resendCooldown);

      this.timer = safeTime;

      if (this.timer <= 0) return;

      this.intervalId = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          clearInterval(this.intervalId);
        }
      }, 1000);
    },
  },
  mounted() {
    if (this.expiredOtp) {
      localStorage.setItem('otpExpiredTime', JSON.stringify(this.expiredOtp));
      this.startTimerFromApi(this.expiredOtp.request_time, this.expiredOtp.expired_time);
    } else {
      this.$router.replace('/login');
    }
    this.$nextTick(() => {
      this.$refs.otpInputs[0]?.focus();
    });
    window.addEventListener('keydown', this.handleEnterKey);
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
    window.removeEventListener('keydown', this.handleEnterKey);
  },
};
</script>

<style scoped>
.otp-card {
  max-width: 450px;
  margin: 0 auto;
}
.btn-gap {
  gap: 16px;
  flex-wrap: wrap;
}
.otp-container {
  gap: 8px;
  flex-wrap: nowrap;
  max-width: 100%;
}
.otp-box {
  width: 48px;
  height: 48px;
  font-size: 20px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: 0.2s;
  flex-shrink: 0;
}
.otp-box:focus {
  border-color: #009688;
  box-shadow: 0 0 0 2px rgba(0, 150, 136, 0.2);
}
.resend-text {
  color: #1976d2;
  cursor: pointer;
  font-weight: bold;
  background: none;
  border: none;
}
.resend-text:hover {
  text-decoration: underline;
}
.resend-text:disabled {
  color: #9e9e9e; 
  cursor: not-allowed;
  font-weight: bold;
  text-decoration: none;
  opacity: 1; 
}

@media (max-width: 400px) {
  .otp-container {
    gap: 6px;
  }
  .otp-box {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
}

@media (max-width: 350px) {
  .otp-container {
    gap: 4px;
  }
  .otp-box {
    width: 38px;
    height: 38px;
    font-size: 16px;
  }
}

@media (max-width: 320px) {
  .otp-container {
    gap: 3px;
  }
  .otp-box {
    width: 34px;
    height: 34px;
    font-size: 14px;
    border-radius: 6px;
  }
}
</style>
